import type { BestLines, BestLine, BookLine } from '@sharpr/types';
import type { ResearchEdge } from '../types/research';

/**
 * Construct a ResearchEdge from a BookLine or BestLine.
 *
 * Explicit field-by-field destructure (not spread) — pairs with the `Omit`
 * shape of `ResearchEdge` so adding a field to `BestLine` fails the TS build
 * here until the conversion is updated. Avoids the prior bug class where a
 * spread silently leaked `isRemoved` / `betLimit` at runtime while the type
 * pretended they didn't exist, and where additions to `BestLine` could ride
 * through as `undefined` despite the type claiming the field was present.
 *
 * `ev` is widened from optional to required: callers (FairValueChip, sort
 * logic) assume a number, so we coerce `bl.ev ?? 0` at this boundary.
 */
export function toResearchEdge(bl: BestLine | BookLine): ResearchEdge {
  return {
    book: bl.book,
    side: bl.side,
    line: bl.line,
    odds: bl.odds,
    pWin: bl.pWin,
    link: bl.link,
    ev: bl.ev ?? 0,
  };
}

/** Pick the best +EV side from best lines */
export function getBestEdge(bestLines?: BestLines): ResearchEdge | undefined {
  const over = bestLines?.over;
  const under = bestLines?.under;
  const overEv = over?.ev ?? 0;
  const underEv = under?.ev ?? 0;

  // Pick the higher EV side, but only if it's positive
  if (overEv > 0 && overEv >= underEv) return toResearchEdge(over!);
  if (underEv > 0) return toResearchEdge(under!);
  return undefined;
}

/** Pick the best side for research display — always returns an edge if any line exists */
export function getResearchEdge(bestLines?: BestLines): ResearchEdge | undefined {
  // Prefer +EV edge
  const evEdge = getBestEdge(bestLines);
  if (evEdge) return evEdge;

  // Fall back to the better (least negative) side
  const over = bestLines?.over;
  const under = bestLines?.under;

  if (over && under) {
    return (over.ev ?? -Infinity) >= (under.ev ?? -Infinity) ? toResearchEdge(over) : toResearchEdge(under);
  }
  if (over) return toResearchEdge(over);
  if (under) return toResearchEdge(under);
  return undefined;
}
